/**
 * 形状工作台的 UI 状态（**纯视图态**：不进文档、不进工程文件、导出零字节影响）。
 *
 * 与 `stores/palette.ts` 的分工相同：这里放"面板记着什么"，文档改动一律走 `useEditor` 的命令。
 * `applied` 只用于行右侧显示"这一组现在是什么形状" —— 它记的是**用户选的形状种类**，
 * 而文档里只能看出标签名（三角和菱形都是 `<polygon>`），所以必须单独记。
 */
import { reactive } from 'vue';
import type { GroupAxis, GroupScope } from '@/core/query/group';
import type { FitMode, ShapeKind } from '@/core/shapes';

export interface ShapeWorkbenchState {
  /** 分组依据：颜色 / 形状 / 大小 */
  axis: GroupAxis;
  /** 范围：只看点状图元（默认）/ 全部图形图元 */
  scope: GroupScope;
  /** 尺寸口径：保持包围盒（默认）/ 保持面积 */
  fit: FitMode;
  /** 当前悬停预览的组 key（行样式用；画布上的预览框与压暗由 useEditor 负责） */
  hover: string | null;
  /** 当前打开了形状浮层的组 key；null = 没开 */
  pickerFor: string | null;
  /** 组 key → 已应用的形状（行右侧显示当前形状） */
  applied: Record<string, ShapeKind>;
  /** 面板底部的一行提示（状态栏是全局提示出口，这里只讲这一步发生了什么） */
  hint: string;
}

export const shapeState = reactive<ShapeWorkbenchState>({
  axis: 'color',
  scope: 'dotted',
  fit: 'bbox',
  hover: null,
  pickerFor: null,
  applied: {},
  hint: '',
});

export const setShapeHint = (text: string): void => {
  shapeState.hint = text;
};

export const markShapeApplied = (key: string, kind: ShapeKind): void => {
  shapeState.applied = { ...shapeState.applied, [key]: kind };
};

/**
 * 换文档时清掉这些状态。
 * 理由与色板一致：「哪一组改成了什么形状」只对当前这份文档有意义，串到下一份文档就是错的。
 * 轴 / 范围 / 尺寸口径是**使用偏好**，不重置。
 */
export function resetShapeDocState(): void {
  shapeState.hover = null;
  shapeState.pickerFor = null;
  shapeState.applied = {};
  shapeState.hint = '';
}
